import { ESectionAlign, ESectionColor, Section } from '@web_opxp/components';
import { useTranslation } from '@web_opxp/features';
import Plyr from 'plyr';
import * as React from 'react';
import './VideoPlayerSection.scss';

export interface IVideoProps {
  // folder where video files are uploaded
  videoPath: string;
  // base name of the video
  videoName: string;
  onCloseHandler: () => void;
  onVideoStartedHandler: () => void;
  onVideoCompletedHandler: () => void;
}

const VideoPlayerSection: React.FC<IVideoProps> = ({
  videoPath,
  videoName,
  onCloseHandler,
  onVideoCompletedHandler,
  onVideoStartedHandler,
}) => {
  const { language, t } = useTranslation();
  const playerRef = React.useRef<Plyr>(undefined);
  let player: Plyr | undefined;
  React.useEffect(() => {
    if (!language) {
      return;
    }
    if (!playerRef.current) {
      // if player not running yet - set it up
      player = new Plyr('#player', {
        controls: [
          'play-large',
          'play',
          'progress',
          'current-time',
          'mute',
          'volume',
          'captions',
          'settings',
          'airplay',
          'fullscreen',
        ],
        captions: {
          active: true,
          language,
        },
        settings: ['captions'],
      });
      player.pip = false;
      player.on('playing', () => {
        onVideoStartedHandler();
      });
      player.on('ended', () => {
        onVideoCompletedHandler();
      });
      playerRef.current = player;
      const startPlaying = async () => {
        if (!playerRef.current) {
          return;
        }
        try {
          await playerRef.current.play();
        } catch {
          // continue regardless of error
        }
      };
      startPlaying();
      player = undefined;
    } else {
      // if player is running - update language
      playerRef.current.language = language;
    }
    return () => {
      // cleanup player on unmount
      if (playerRef.current) {
        playerRef.current = undefined;
      }
      player = undefined;
    };
  }, [onVideoStartedHandler, onVideoCompletedHandler, language, playerRef, player]);
  // set language suffix with fallback to german
  const langSuffix = language ?? 'de';
  // generate complete path to required video
  const videoSrc = `${videoPath}/${videoName}_video_${langSuffix}.mp4`;
  // generate path to subtitles without language suffix and extension
  const captionsPath = `${videoPath}/${videoName}_subtitle`;
  return (
    <Section color={ESectionColor.blue} align={ESectionAlign.center} className="video-section">
      <div className="video-wrapper" data-testid="video-wrapper">
        <video
          disablePictureInPicture
          crossOrigin="anonymous"
          playsInline
          src={videoSrc}
          id="player"
          controlsList="nodownload">
          <track
            kind="captions"
            label={t('SubtitlesLabel.English') ?? ''}
            src={`${captionsPath}_en.vtt`}
            srcLang="en"
            data-testid="video-track"
          />
          <track
            kind="captions"
            label={t('SubtitlesLabel.German') ?? ''}
            src={`${captionsPath}_de.vtt`}
            srcLang="de"
            data-testid="video-track"
          />
          <track
            kind="captions"
            label={t('SubtitlesLabel.French') ?? ''}
            src={`${captionsPath}_fr.vtt`}
            srcLang="fr"
            data-testid="video-track"
          />
          <track
            kind="captions"
            label={t('SubtitlesLabel.Russian') ?? ''}
            src={`${captionsPath}_ru.vtt`}
            srcLang="ru"
            data-testid="video-track"
          />
        </video>
        <span className="plyr__control close-button" data-testid="video-close-button" onClick={onCloseHandler}>
          <svg
            role="img"
            className="close-button-icon"
            width="100%"
            height="100%"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg">
            <path fill="currentColor" id="svg_3" d="m0,0l50,50" strokeWidth="6" stroke="currentColor" />
            <path fill="currentColor" id="svg_4" d="m50,0l-50,50" strokeWidth="6" stroke="currentColor" />
          </svg>
        </span>
      </div>
    </Section>
  );
};

export default VideoPlayerSection;
